import type {
  MigrationStatusEnum,
  UpdateMigrationRequest,
  UpdateMigrationResponse,
  TransformationConfig
} from '../../types';
import { logger } from '../../utils/logger';
import { MigrationStatus } from '../../types/migration';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

// Re-export for backward compatibility
export { MigrationStatusEnum as MigrationStatus };
export type { TransformationConfig };

/**
 * Updates an existing migration with new configuration, transformations, or status
 */
const updateMigration = async (
  bearerToken: string,
  migrationId: string,
  request: UpdateMigrationRequest
): Promise<UpdateMigrationResponse> => {
  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${migrationId}`;

    const response = await authenticatedFetch(url, {
      method: 'PUT',
      headers: {
        ...getV2AuthHeaders(bearerToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      logger.error('Update migration API returned error', {
        status: response.status,
        statusText: response.statusText,
        migrationId,
        url,
        requestType: request.advanceToNextStatus ? 'advanceStatus' : 'update'
      });
      throw new Error(`Error updating migration: ${response.status} ${response.statusText}`);
    }

    const data: UpdateMigrationResponse = await response.json();
    logger.info('Migration updated successfully', {
      migrationId,
      newStatus: data.status,
      hasTransformations: !!request.transformationConfig
    });
    return data;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to update migration', {
      error: msg,
      migrationId,
      url: `${getEnv().VITE_EDCI_API_URL}/api/migrations/${migrationId}`
    });
    throw error;
  }
};

/**
 * Updates migration with transformation config and advances to next status
 */
export const approveExportAndStartTransformation = async (
  bearerToken: string,
  migrationId: string,
  transformationConfig: TransformationConfig
): Promise<UpdateMigrationResponse> => {
  return updateMigration(bearerToken, migrationId, {
    transformationConfig,
    advanceToNextStatus: true
  });
};

/**
 * Approves export with an empty transformation config and jumps directly to ImportQueued,
 * bypassing the transformation pipeline entirely when no transformations are needed.
 */
export const approveExportAndSkipToImport = async (
  bearerToken: string,
  migrationId: string,
  transformationConfig: TransformationConfig
): Promise<UpdateMigrationResponse> => {
  return updateMigration(bearerToken, migrationId, {
    transformationConfig,
    status: MigrationStatus.ImportQueued
  });
};

/**
 * Advances migration to next status after approving transformations.
 * When `dryRun` is provided, it is applied at import-start time so the operator can decide
 * to preview (Terraform plan only) or run a real import from the transformation-approval step.
 */
export const approveTransformationsAndStartImport = async (
  bearerToken: string,
  migrationId: string,
  dryRun?: boolean
): Promise<UpdateMigrationResponse> => {
  return updateMigration(bearerToken, migrationId, {
    advanceToNextStatus: true,
    ...(dryRun !== undefined && { dryRun })
  });
};

/**
 * Flips the import dry-run (preview) flag without advancing status. Used to convert a completed
 * dry-run preview into a real import (pair with POST /start-import via runRealImport).
 */
export const setImportDryRun = async (
  bearerToken: string,
  migrationId: string,
  dryRun: boolean
): Promise<UpdateMigrationResponse> => {
  return updateMigration(bearerToken, migrationId, { dryRun });
};

// Statuses that indicate the approve-export step needs to be re-run
const RERUN_STATUSES = ['importfailed', 'validationfailed', 'transformationfailed', 'failed'];

/**
 * Returns true when the migration status indicates export approval needs to be re-run
 * (e.g. because a previous import or validation attempt failed).
 * Mirrors the "isPrePopulatedForm" pattern used by MigrationSetup and SnapshotSetup.
 */
export const isApproveExportRerunStatus = (status: string | number): boolean => {
  if (typeof status !== 'string') return false;
  return RERUN_STATUSES.includes(status.toLowerCase());
};

/**
 * Re-runs export approval with transformations from a failed state.
 * Resets the migration back to ExportComplete and advances through the transformation pipeline.
 * Use when transformations are present and migration is in ImportFailed / ValidationFailed state.
 */
export const rerunApproveExportWithTransformation = async (
  bearerToken: string,
  migrationId: string,
  transformationConfig: TransformationConfig
): Promise<UpdateMigrationResponse> => {
  return updateMigration(bearerToken, migrationId, {
    transformationConfig,
    status: MigrationStatus.ExportComplete,
    advanceToNextStatus: true
  });
};

/**
 * Re-runs export approval without transformations (edit mode where user removed all transformations).
 * Sets an empty transformation config and jumps directly to ImportQueued, skipping the transformation pipeline.
 * Use when editing from ApproveTransformationsPage and user deletes all transformations.
 */
export const rerunApproveExportWithoutTransformation = async (
  bearerToken: string,
  migrationId: string
): Promise<UpdateMigrationResponse> => {
  return updateMigration(bearerToken, migrationId, {
    transformationConfig: {
      transformations: [],
      settings: {
        logLevel: 'Information',
        validateBeforeApply: true,
        createBackups: false,
        stopOnError: false,
        maxParallelOperations: 1
      }
    },
    status: MigrationStatus.ImportQueued
  });
};

export default updateMigration;
